import React, { useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { ElectionContainer } from '../election/ElectionContainer';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { NotFound } from '../NotFound';
import { CandidateEditStatus } from '../../_generatedTypes.g';
import { DocumentTitle } from '../DocumentTitle';
import { QuestionMark } from '../ui/QuestionMark';
import { FileInput } from '../ui/FileInput';
import { RenderErrors } from '../ui/ErrorComponent';
import { ElectionNameAndCandidateName } from '../candidate/ElectionNameAndCandidateName';
import { canEditCandidateText } from '../../selectors';
import { useDispatch } from 'react-redux';
import { getTranslator } from '../../Translator';
import { getAvatarUrl } from '../candidate/CandidateProfile';
import { addCandidateSystemCandidate } from '../../features/candidate/candidatesSlice';

import { candidateEdited, saveCandidate, startEditingParty } from '../../features/candidate/candidateEditSlice';
import { useAppSelector } from '../../hooks';


type CandidateEditComponentParams = {
    candidateId: string,
};

export const CandidateEditComponent = () => {
    const enableCandidateSlogan = useAppSelector(state => state.clientState.EnableCandidateSlogan);
    const enableCandidateElectableInOthers = useAppSelector(state => state.clientState.EnableCandidateElectableInOthers);
    const justDutch = useAppSelector(state => state.clientState.JustDutch);
    const account = useAppSelector(state => state.clientState.Account);
    const language = useAppSelector(state => state.language);
    const editingCandidateById = useAppSelector(state => state.editingCandidateById);
    const candidates = useAppSelector(state => state.candidates);
    const parties = useAppSelector(state => state.parties);
    const elections = useAppSelector(state => state.elections);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);

    const dispatch = useDispatch();
    const t = getTranslator(language);
    const navigate = useNavigate();

    const candidateId = Number(useParams<CandidateEditComponentParams>().candidateId);
    const possibleEditableCandidate = editingCandidateById[candidateId];


    const isCandidateAddCandidateSystem = candidateId == 0;

    const isCandidateEditable = canEditCandidateText(account, candidates, parties, elections, candidateId);

    const editingCandidate = isCandidateAddCandidateSystem || isCandidateEditable ? possibleEditableCandidate : undefined;

    useEffect(() => {
        if (!editingCandidate) {
            startEditingParty(candidateId, candidates, dispatch);
        }
    }, [editingCandidate])

    if (!editingCandidate) {
        return <NotFound />;
    }

    const onSelectAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        //const { editCandidate, candidateId } = this.props;
        const file = e.target.files![0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = function () {
                dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { Avatar: (reader.result as string).split(',')[1], HasAvatar: true } }));
            }
            reader.readAsDataURL(file);
        } else {
            dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { Avatar: undefined, HasAvatar: false } }));
        }
    };

    return <ElectionContainer candidateId={candidateId}>
        <div>
            <h1>{isCandidateAddCandidateSystem ? t['My candidacy data'] : t['Edit candidate data']}</h1>
            <DocumentTitle title={isCandidateAddCandidateSystem ? t['My candidacy data'] : t['Edit candidate data']} />
            <ElectionNameAndCandidateName candidateId={candidateId} />
            {isCandidateAddCandidateSystem && <div className="candidate__editor">
                <label>
                    {t['Name']}
                    <input
                        lang="NL"
                        placeholder={t['Name']}
                        defaultValue={editingCandidate.Name}
                        maxLength={400}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { Name: e.currentTarget.value } }))} />
                </label>
            </div>
            }
            {account.IsAdmin && <div className="candidate__editor">
                <QuestionMark>{t['If username is filled, the candidate can edit his or her own data if logged in with this username']}</QuestionMark>
                <label>
                    {t['User name']}
                    <input
                        lang="NL"
                        placeholder={t['User name']}
                        defaultValue={editingCandidate.UserName}
                        maxLength={400}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { UserName: e.currentTarget.value } }))} />
                </label>
            </div>
            }
            {enableCandidateSlogan && <div className="candidate__editor">
                <label>
                    {t.columnDescription(t['Slogan'], 'NL', 200)}
                    <TextareaAutosize
                        lang="NL"
                        placeholder={t.columnDescription(t['Slogan'], 'NL', 200)}
                        defaultValue={editingCandidate.Slogan_NL}
                        maxLength={200}
                        minRows={5}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { Slogan_NL: e.currentTarget.value } }))} />
                </label>
                {!justDutch && <label>
                    {t.columnDescription(t['Slogan'], 'EN', 200)}
                    <TextareaAutosize
                        lang="EN"
                        placeholder={t.columnDescription(t['Slogan'], 'EN', 200)}
                        defaultValue={editingCandidate.Slogan_EN}
                        maxLength={200}
                        minRows={5}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { Slogan_EN: e.currentTarget.value } }))} />
                </label>}
            </div>}
            <div className="candidate__editor">
                <label>
                    {t.columnDescription(t['Election text'], 'NL', 4000)}
                    <TextareaAutosize
                        lang="NL"
                        placeholder={t.columnDescription(t['Election text'], 'NL', 4000)}
                        defaultValue={editingCandidate.ElectionText_NL}
                        maxLength={4000}
                        minRows={10}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { ElectionText_NL: e.currentTarget.value } }))} />
                </label>
            </div>
            {!justDutch && <div className="candidate__editor">
                <label>
                    {t.columnDescription(t['Election text'], 'EN', 4000)}
                    <TextareaAutosize
                        lang="EN"
                        placeholder={t.columnDescription(t['Election text'], 'EN', 4000)}
                        defaultValue={editingCandidate.ElectionText_EN}
                        maxLength={4000}
                        minRows={10}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { ElectionText_EN: e.currentTarget.value } }))} />
                </label>
            </div>}
            <QuestionMark>{t['Photo must be in JPG format with a maximum file size of 1MB']}</QuestionMark>
            <div className="candidate__editor">
                <label>
                    {t['Photograph']}
                    {editingCandidate.Avatar != null || editingCandidate.HasAvatar ?
                        <img src={editingCandidate.Avatar
                            ? 'data:image/jpeg;base64,' + editingCandidate.Avatar
                            : getAvatarUrl(editingCandidate)} />
                        : ''}
                    <FileInput
                        accept=".jpg, .jpeg"
                        name="avatar_upload"
                        onChange={onSelectAvatar}
                        caption={t['Choose image file']} />
                </label>
            </div>
            {enableCandidateElectableInOthers && <div className="candidate__editor">
                <label>
                    {t['Also electable in (NL, max. 200 characters)']}
                    <input name="ElectableInOther_NL" type="text" lang="nl-NL" maxLength={200} value={editingCandidate.ElectableInOther_NL}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { ElectableInOther_NL: e.target.value } }))} />
                </label>
                {!justDutch && <label>
                    {t['Also electable in (EN, max. 200 characters)']}
                    <input name="ElectableInOther_EN" type="text" lang="en-GB" maxLength={200} value={editingCandidate.ElectableInOther_EN}
                        onChange={e => dispatch(candidateEdited({ candidateId: candidateId, candidateChanges: { ElectableInOther_EN: e.target.value } }))} />
                </label>}
            </div>}
            {(() => {
                if (editingCandidate.EditStatus == CandidateEditStatus.Success) {
                    return <div className="statusbanner--ok">{t['Your changes were successfully saved.']}</div>;
                } else if (editingCandidate.EditStatus == CandidateEditStatus.Saving) {
                    return <div className="statusbanner--ok">{t['Saving...']}</div>;
                } else if (editingCandidate.EditStatus == CandidateEditStatus.Error) {
                    return <div className="statusbanner--warning">
                        {t['An error occurred during saving.']}
                        <RenderErrors errors={editingCandidate.ModelErrors} />
                    </div>
                } else {
                    return null;
                }
            })()}
            <div className="buttonrow">
                <button type="button" onClick={() => isCandidateAddCandidateSystem ? addCandidateSystemCandidate(editingCandidate, token, dispatch, navigate) : saveCandidate(editingCandidateById[candidateId], token, dispatch)}>{t['Save']}</button>
                <Link className="linkbutton" to={isCandidateAddCandidateSystem ? `/election/${editingCandidate.ElectionId}` : `/candidate/${editingCandidate.CandidateId}`}>{t['Back']}</Link>
            </div>
        </div>
    </ElectionContainer>
}


